import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Button, Dropdown, Input, Space} from 'antd';
import {DownOutlined} from '@ant-design/icons';
import Paragraph from 'antd/es/typography/Paragraph';
import {
  StyledVendorSelect,
  StyledVendorSelectMenu,
  StyledVendorSelectMenuItem,
  StyledVendorDropdownContent,
  StyledVendorSelectDropdownContainer,
  StyledVendorSelectSearchContainer,
  StyledVendorSelectInputContent,
  StyledVendorSelectInputContentValue,
  StyledVendorSelectInputContentValueTitle,
  StyledVendorSelectInputPlaceholder,
  StyledVendorSelectInputContentValueIban,
} from './StyledVendorSelect';
import {useOnClickOutside} from '../../../../hooks';
import {vendorsHelpers} from '../../../../utils/helpers';


const VendorSelect = ({
  customSelectedRenderer,
  disabled = false,
  emptyText = 'No results found',
  isEnableAdd = false,
  isEnableAddVat = false,
  onAdd,
  onChange,
  placeholder,
  showArrow = true,
  value,
  vendors,
  ...rest
}) => {
  const [t] = useTranslation('main');
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);
  const [componentValue, setComponentValue] = useState(undefined);
  const [options, setOptions] = useState([]);
  const [searchedOptions, setSearchedOptions] = useState(options);
  const [searchedValue, setSearchedValue] = useState('');
  const [isUpdated, setIsUpdated] = useState(false);

  useEffect(() => {
    if (value !== componentValue) {
      setComponentValue(value);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSearchedOptions(options);
    if (searchedValue) setSearchedValue('');
  }, [options]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpenDropdown && isUpdated && onChange) {
      setIsUpdated(false);
      onChange && onChange(componentValue);
    }
  }, [isOpenDropdown]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const options = vendors.data.map(vendor => ({
      iban: vendor.iban,
      label: (
        <Space>
          <span>{vendor.name}</span>
          {vendorsHelpers.getVendorTypeIcon(vendor)}
        </Space>
      ),
      name: vendor.name,
      value: vendor.vendor_id,
    }));
    setOptions(options);
  }, [vendors?.data]);

  const handleOpenDropdown = () => {
    if (disabled) return;
    !isOpenDropdown && setIsOpenDropdown(true);
  }

  const handleMenuClick = ({key, domEvent}) => {
    setComponentValue(key);
    setIsUpdated(true);
    setIsOpenDropdown(false);
    domEvent.preventDefault();
    domEvent.stopPropagation();
  }

  const onClickOutside = (e) => {
    const parentSelector = '.ant-dropdown';
    if (!e.target.closest(parentSelector)) setIsOpenDropdown(false);
  }

  const onSearch = (e) => {
    let {value} = e.target;
    setSearchedValue(value);

    value = value.toLowerCase();
    setSearchedOptions(options.filter(o => o.name.toLowerCase().indexOf(value) >= 0));
  }

  const handleOnAddClick = (e, size) => {
    e.stopPropagation();
    if (!onAdd) return;

    onAdd(e, size);
    setIsOpenDropdown(false);
  }

  const searchedOptionsLength = searchedOptions.length;

  const searchComponent = (
    <Input
      allowClear={true}
      size='large'
      placeholder={t('search')}
      onChange={onSearch}
      value={searchedValue}
    />
  );

  const getInputContent = () => {
    const placeholder = (
      <StyledVendorSelectInputPlaceholder>
        Select vendor
      </StyledVendorSelectInputPlaceholder>
    );

    if (componentValue) {
      const option = options.find(option => option.value === componentValue);

      return (
        <StyledVendorSelectInputContentValue>
          {option ? (
            customSelectedRenderer
              ? customSelectedRenderer(option)
              : (
                <Space direction='vertical' size={0}>
                  <Space size='small'>
                    <StyledVendorSelectInputContentValueTitle>
                      {t('name')}:
                    </StyledVendorSelectInputContentValueTitle>
                    <span>{option.name}</span>
                  </Space>
                  <Space size='small' align='center'>
                    <StyledVendorSelectInputContentValueTitle>
                      {t('iban')}:
                    </StyledVendorSelectInputContentValueTitle>
                    <StyledVendorSelectInputContentValueIban>
                      {option.iban}
                    </StyledVendorSelectInputContentValueIban>
                  </Space>
                </Space>
              )
          ): placeholder}
        </StyledVendorSelectInputContentValue>
      );
    } else {
      return placeholder;
    }
  }

  return (
    <StyledVendorSelect
      {...rest}
    >
      <StyledVendorSelectDropdownContainer
        ref={useOnClickOutside(onClickOutside, isOpenDropdown, null, true)}
      >
        <Dropdown
          classNames={{root: 'tag-select-overlay'}}
          popupRender={() => (
            <StyledVendorDropdownContent>
              <StyledVendorSelectSearchContainer className='search'>
                {searchComponent}
              </StyledVendorSelectSearchContainer>
              <div className='dropdown-searched-content'>
                {isEnableAdd && (
                  <Button
                    type='link'
                    onClick={(e) => handleOnAddClick(e)}
                  >
                    + {t('addNewVendor')}
                  </Button>
                )}
                {isEnableAddVat && (
                  <Button
                    type='link'
                    onClick={(e) => handleOnAddClick(e, 'vat')}
                  >
                    + {t('add')} {t('vat')}
                  </Button>
                )}
                {searchedOptionsLength > 0 && (
                  <StyledVendorSelectMenu
                    onClick={handleMenuClick}
                    selectedKeys={[componentValue]}
                  >
                    {searchedOptions.map(item => {
                      const {label, value} = item;
                      return (
                        <StyledVendorSelectMenuItem
                          key={value}
                        >
                          {label}
                        </StyledVendorSelectMenuItem>
                      );
                    })}
                  </StyledVendorSelectMenu>
                )}

                {searchedOptionsLength === 0 && searchedOptionsLength !== options.length && (
                  <Paragraph>
                    {emptyText}
                  </Paragraph>
                )}
              </div>
            </StyledVendorDropdownContent>
          )}
          open={isOpenDropdown}
          placement='bottomLeft'
        >
          <StyledVendorSelectInputContent className={disabled && 'disabled-vendor-select'} onClick={handleOpenDropdown}>
            {getInputContent()}
            {showArrow && (
              <DownOutlined
                style={{ color: 'rgba(0,0,0,.45)', width: 12, height: 12 }}
              />
            )}
          </StyledVendorSelectInputContent>
        </Dropdown>
      </StyledVendorSelectDropdownContainer>

    </StyledVendorSelect>
  );
};

VendorSelect.propTypes = {
  customSelectedRenderer: PropTypes.func,
  disabled: PropTypes.bool,
  emptyText: PropTypes.string,
  isEnableAdd: PropTypes.bool,
  isEnableAddVat: PropTypes.bool,
  onAdd: PropTypes.func,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  showArrow: PropTypes.bool,
  vendors: PropTypes.shape({
    data: PropTypes.array,
    loading: PropTypes.bool,
  })
};

export default VendorSelect;
